import { SelfContainedCommand } from '../../../types/commands';
import { createSuccessResult, createErrorResult } from '../../../types/standardToolResults';
import { getDocsAppStore } from '../../DocsApp/store/docsAppStore';
import useEntityStore from '../../../stores/entityStore';
import { useSpaceStore } from '../../../stores/spaceStore';

/**
 * Format citation text based on style
 */
function formatCitation(
  references: any[],
  style: string,
  locators: any[]
): string {
  if (style === 'author-year') {
    const citations = references.map(ref => {
      const authors = ref.skeleton?.authors || [];
      const year = ref.skeleton?.year || 'n.d.';
      const locator = locators.find(l => l.referenceId === ref._id);
      
      let citation: string;
      if (authors.length === 0) {
        citation = `Unknown, ${year}`;
      } else if (authors.length === 1) {
        const lastName = authors[0].split(' ').pop() || 'Unknown';
        citation = `${lastName}, ${year}`;
      } else if (authors.length === 2) {
        const lastName1 = authors[0].split(' ').pop() || '';
        const lastName2 = authors[1].split(' ').pop() || '';
        citation = `${lastName1} & ${lastName2}, ${year}`;
      } else {
        const lastName = authors[0].split(' ').pop() || '';
        citation = `${lastName} et al., ${year}`;
      }
      
      if (locator?.pages) {
        citation += `, ${locator.pages}`;
      }
      
      return citation;
    });
    
    return `(${citations.join('; ')})`;
  }
  
  // Numeric style
  return `[${references.map((_, i) => i + 1).join(', ')}]`;
}

/**
 * AI command to insert citations in River
 * 
 * Example AI usage:
 *   AI writes: "Autism interventions have shown effectiveness"
 *   AI thinks: "I should cite Smith 2020 and Jones 2021 here"
 *   Tool: library_insert_citation(referenceIds=["ref-123", "ref-456"])
 *   Result: Text becomes "...effectiveness (Smith, 2020; Jones, 2021)"
 */
export const libraryInsertCitationCommand: SelfContainedCommand = {
  id: 'library_insert_citation',
  description: 'Insert a citation in the current document at the cursor position. Can cite multiple references at once. The citation will be formatted according to the document\'s citation style.',
  
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',
  
  schema: {
    parameters: [
      {
        name: 'referenceIds',
        type: 'array',
        description: 'Array of reference entity IDs to cite. Get these from library_search_references.',
        required: true
      },
      {
        name: 'position',
        type: 'string',
        description: 'Where to insert: "cursor" (default) or "end"',
        required: false
      },
      {
        name: 'locators',
        type: 'array',
        description: 'Optional page numbers or sections for each reference',
        required: false
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { referenceIds: ['ref-abc123'] },
        expectedOutput: {
          status: 'success',
          message: 'Inserted citation: (Smith, 2020)',
          format: 'json',
          data: { citationId: 'cite-123', formattedText: '(Smith, 2020)' }
        },
        description: 'Insert single citation'
      },
      {
        input: { referenceIds: ['ref-123', 'ref-456', 'ref-789'] },
        expectedOutput: {
          status: 'success',
          message: 'Inserted citation: (Smith, 2020; Jones, 2021; Brown, 2019)',
          format: 'json',
          data: { citationId: 'cite-456', referenceCount: 3 }
        },
        description: 'Insert multiple citations'
      }
    ],
    bad: []
  },
  
  handler: async (params, context) => {
    try {
      const { referenceIds, position = 'cursor', locators = [] } = params;
      const { spaceContext } = context;
      const windowId = spaceContext?.activeWindowId;
      
      // Validate we're in a docs window
      if (!windowId) {
        return createErrorResult(
          'No active document window',
          'Cannot insert citation without an active document',
          'validation'
        );
      }
      
      // Get the docs store for this window
      const storeMap = (getDocsAppStore as any).storeMap;
      if (!storeMap) {
        return createErrorResult(
          'Cannot access docs store',
          'Docs app store not initialized',
          'execution'
        );
      }
      
      // Find store for this window
      let editorView = null;
      for (const [key, store] of storeMap) {
        if (key.startsWith(windowId)) {
          editorView = (store as any).getState?.()?.editorView;
          if (editorView) break;
        }
      }
      
      if (!editorView) {
        return createErrorResult(
          'No active editor view',
          'Could not find active editor for this window',
          'execution'
        );
      }
      
      // Check if schema has citation mark
      if (!editorView.state.schema.marks.citation) {
        return createErrorResult(
          'Citation mark not available',
          'The citation mark is not registered in the editor schema. Make sure Library plugin is initialized.',
          'execution'
        );
      }
      
      // Fetch reference entities to get metadata
      const references = await Promise.all(
        referenceIds.map((id: string) => {
          const { actions } = useEntityStore.getState();
          return actions.getEntity(id);
        })
      );
      
      // Format citation text based on style
      const citationStyle = 'author-year'; // TODO: Get from doc settings
      const formattedText = formatCitation(references, citationStyle, locators);
      
      // Generate unique citation ID
      const citationId = `cite-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      // Create citation mark
      const mark = editorView.state.schema.marks.citation.create({
        referenceIds,
        style: citationStyle,
        formattedText,
        locators,
        citationId
      });
      
      // Insert citation at cursor or end
      const { state } = editorView;
      const { tr } = state;
      
      let insertPos: number;
      if (position === 'end') {
        insertPos = state.doc.content.size - 1;
      } else {
        insertPos = state.selection.to;
      }
      
      // Insert citation text with mark
      tr.insert(insertPos, state.schema.text(formattedText, [mark]));
      
      // Dispatch transaction
      editorView.dispatch(tr);
      
      // Update document's cited references
      // Get document entity from window
      try {
        const { windows, getWindowEntity } = useSpaceStore.getState();
        const window = windows[windowId];
        const docEntity = getWindowEntity?.(windowId) || window?.entity;
        
        if (docEntity?._id) {
          const { actions } = useEntityStore.getState();
          const doc = await actions.getEntity(docEntity._id);
          const existingRefs = doc.skeleton?.citedReferences || [];
          const updatedRefs = Array.from(new Set([...existingRefs, ...referenceIds]));
          
          await actions.updateEntity(docEntity._id, {
            skeleton: {
              ...doc.skeleton,
              citedReferences: updatedRefs
            }
          });
        }
      } catch (error) {
        // Non-fatal - citation still inserted
        console.error('[LibraryInsertCitation] Failed to update document citations:', error);
      }
      
      return createSuccessResult(
        `Inserted citation: ${formattedText}`,
        {
          citationId,
          formattedText,
          referenceCount: referenceIds.length
        }
      );
    } catch (error) {
      return createErrorResult(
        'Failed to insert citation',
        error instanceof Error ? error.message : 'Unknown error',
        'execution'
      );
    }
  }
};

